import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {VetClinicService} from './vet-clinic.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard realizujący kontrolę dostępu do modułu Zarządzania Użytkownikami.
 * Weryfikuje autoryzację po stronie serwera API, upewniając się, że aktywna sesja
 * posiada wymagane role systemowe (RBAC) do administrowania kontami użytkowników.
 */
@Injectable({
  providedIn: 'root'
})
export class UsersAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private vetClinicService: VetClinicService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Ewaluuje uprawnienia przed asynchronicznym pobraniem (Lazy Loading) paczki modułu użytkowników.
   * Zapobiega transferowi logiki administracyjnej do przeglądarki klienta, weryfikując
   * żądanie na dedykowanym endpointcie API przed inicjalizacją pobierania kodu JavaScript.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku posiadania odpowiedniej roli).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/users-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - users - ok');
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia w momencie próby aktywacji i wyrenderowania interfejsu
   * zarządzania użytkownikami. Obejmuje obsługę wygaśnięcia sesji (HTTP 401)
   * poprzez bezpieczne wylogowanie oraz obsługuje braki w uprawnieniach aplikacyjnych
   * przy pomocy ustandaryzowanych powiadomień.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/users-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - users - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
